import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Play,
  XCircle,
  CheckCircle2,
  AlertTriangle,
  Factory,
  Timer,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Checkbox } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { ConfirmDialog, Dialog, Alert } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { productionService } from '@services/productionService';
import {
  ProductionOrder,
  ProductionActivityLog,
  getProductionOrderStatusLabel,
  getProductionOrderStatusBadgeClass,
} from '@/types/production';

/** Formata data ISO para dd/mm/aaaa sem deslocamento de fuso (parse manual para datas puras) */
const formatDate = (value?: string): string => {
  if (!value) return '-';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(Z|\.000Z)?$/.exec(value);
  if (dateOnly) {
    return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    const fallback = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    return fallback ? `${fallback[3]}/${fallback[2]}/${fallback[1]}` : '-';
  }
  return date.toLocaleDateString('pt-BR');
};

/** Formata data/hora ISO para dd/mm/aaaa hh:mm */
const formatDateTime = (value?: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (isNaN(date.getTime())) return formatDate(value);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatQty = (value?: number): string =>
  value !== undefined && value !== null ? value.toLocaleString('pt-BR') : '0';

interface CompleteItemRow {
  production_order_item_id: number;
  code: string;
  description: string;
  quantity_planned: number;
  quantity_produced: string;
  quantity_lost: string;
}

/**
 * Detalhe da Ordem de Produção — informações, itens, pedidos vinculados,
 * apontamentos e ações por status (iniciar, editar, cancelar, concluir).
 */
export const ProductionOrderDetailView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [order, setOrder] = useState<ProductionOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Apontamentos da OP
  const [logs, setLogs] = useState<ProductionActivityLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Confirmações
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Conclusão
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [completeItems, setCompleteItems] = useState<CompleteItemRow[]>([]);
  const [consumeMaterials, setConsumeMaterials] = useState(true);
  const [completeError, setCompleteError] = useState<string | null>(null);

  const loadOrder = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const result = await productionService.getProductionOrder(parseInt(id));
      setOrder(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar ordem de produção');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    if (!id) return;
    try {
      setLogsLoading(true);
      const result = await productionService.listActivityLogs({
        page: 1,
        pageSize: 100,
        production_order_id: parseInt(id),
      });
      setLogs(result.data);
    } catch {
      // Falha silenciosa — seção de apontamentos é auxiliar
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    loadOrder();
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ============================================================
  // Ações
  // ============================================================
  const handleStart = async () => {
    if (!order) return;
    try {
      setActionLoading(true);
      setError(null);
      const updated = await productionService.startProductionOrder(order.id);
      setOrder(updated);
      setShowStartConfirm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao iniciar a produção');
      setShowStartConfirm(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!order) return;
    try {
      setActionLoading(true);
      setError(null);
      const updated = await productionService.cancelProductionOrder(order.id);
      setOrder(updated);
      setShowCancelConfirm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao cancelar a ordem de produção');
      setShowCancelConfirm(false);
    } finally {
      setActionLoading(false);
    }
  };

  const openCompleteModal = () => {
    if (!order) return;
    setCompleteError(null);
    setConsumeMaterials(true);
    setCompleteItems(
      (order.items || []).map((item) => ({
        production_order_item_id: item.id,
        code: item.target_code || String(item.product_id || item.raw_material_id),
        description: item.target_description || '',
        quantity_planned: item.quantity_planned,
        quantity_produced: String(item.quantity_planned ?? 0),
        quantity_lost: '0',
      }))
    );
    setShowCompleteModal(true);
  };

  const updateCompleteItem = (
    itemId: number,
    field: 'quantity_produced' | 'quantity_lost',
    value: string
  ) => {
    setCompleteItems((prev) =>
      prev.map((row) =>
        row.production_order_item_id === itemId ? { ...row, [field]: value } : row
      )
    );
  };

  const validateCompletion = (): string | null => {
    for (const row of completeItems) {
      const produced = parseInt(row.quantity_produced, 10);
      const lost = parseInt(row.quantity_lost, 10);
      if (isNaN(produced) || produced < 0) {
        return `Quantidade produzida do item "${row.code}" deve ser maior ou igual a zero`;
      }
      if (isNaN(lost) || lost < 0) {
        return `Quantidade perdida do item "${row.code}" deve ser maior ou igual a zero`;
      }
    }
    return null;
  };

  const handleRequestComplete = () => {
    const validationError = validateCompletion();
    if (validationError) {
      setCompleteError(validationError);
      return;
    }
    setCompleteError(null);
    setShowCompleteConfirm(true);
  };

  const handleComplete = async () => {
    if (!order) return;
    try {
      setActionLoading(true);
      setCompleteError(null);
      const updated = await productionService.completeProductionOrder(order.id, {
        items: completeItems.map((row) => ({
          production_order_item_id: row.production_order_item_id,
          quantity_produced: parseInt(row.quantity_produced, 10),
          quantity_lost: parseInt(row.quantity_lost, 10),
        })),
        consume_materials: consumeMaterials,
      });
      setOrder(updated);
      setShowCompleteConfirm(false);
      setShowCompleteModal(false);
    } catch (err) {
      setCompleteError(err instanceof Error ? err.message : 'Erro ao concluir a produção');
      setShowCompleteConfirm(false);
    } finally {
      setActionLoading(false);
    }
  };

  const getOrderNumber = (so: { order_id: number; order_num_erp?: string; order_num_ecommerce?: string }) =>
    so.order_num_erp || so.order_num_ecommerce || String(so.order_id);

  // ============================================================
  // Render
  // ============================================================
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/production/orders')}>
            <ArrowLeft size={20} />
          </Button>
          <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
        </div>
        <Card>
          <CardContent>
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-14 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/production/orders')}>
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-h1">Ordem de Produção</h1>
        </div>
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={18} />
              <p className="text-red-600">{error || 'Ordem de produção não encontrada'}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPlanned = order.status === 0;
  const isInProduction = order.status === 1;
  const isEditable = isPlanned || isInProduction;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/production/orders')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-h1">{order.code}</h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getProductionOrderStatusBadgeClass(
                  order.status
                )}`}
              >
                {getProductionOrderStatusLabel(order.status)}
              </span>
            </div>
            <p className="text-slate-600">{order.description || 'Ordem de produção'}</p>
          </div>
        </div>

        {/* Ações por status */}
        <div className="flex flex-wrap items-center gap-2">
          {isPlanned && (
            <PermissionGuard permission="production.write">
              <Button
                onClick={() => setShowStartConfirm(true)}
                icon={<Play size={18} />}
                disabled={actionLoading}
              >
                Iniciar Produção
              </Button>
            </PermissionGuard>
          )}
          {isInProduction && (
            <PermissionGuard permission="production.write">
              <Button
                variant="success"
                onClick={openCompleteModal}
                icon={<CheckCircle2 size={18} />}
                disabled={actionLoading}
              >
                Concluir
              </Button>
            </PermissionGuard>
          )}
          {isEditable && (
            <>
              <PermissionGuard permission="production.write">
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/production/orders/${order.id}/edit`)}
                  icon={<Edit size={18} />}
                  disabled={actionLoading}
                >
                  Editar
                </Button>
              </PermissionGuard>
              <PermissionGuard permission="production.write">
                <Button
                  variant="outline"
                  onClick={() => setShowCancelConfirm(true)}
                  icon={<XCircle size={18} />}
                  disabled={actionLoading}
                >
                  Cancelar
                </Button>
              </PermissionGuard>
            </>
          )}
        </div>
      </div>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={18} />
              <p className="text-red-600">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cards de informações */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent>
            <p className="text-small text-slate-500">Cor do Floco</p>
            <p className="text-lg font-semibold text-slate-900">{order.flock_color || '-'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-small text-slate-500">Período Planejado</p>
            <p className="text-lg font-semibold text-slate-900">
              {formatDate(order.planned_start_date)} — {formatDate(order.planned_end_date)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-small text-slate-500">Iniciada em</p>
            <p className="text-lg font-semibold text-slate-900">
              {formatDateTime(order.started_at)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-small text-slate-500">Concluída em</p>
            <p className="text-lg font-semibold text-slate-900">
              {formatDateTime(order.completed_at)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Totais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-50">
          <CardContent>
            <p className="text-small text-slate-500">Total Planejado</p>
            <p className="text-2xl font-bold text-slate-900">{formatQty(order.total_planned)}</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent>
            <p className="text-small text-slate-500">Total Produzido</p>
            <p className="text-2xl font-bold text-emerald-700">
              {formatQty(order.total_produced)}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-red-50">
          <CardContent>
            <p className="text-small text-slate-500">Total Perdido</p>
            <p className="text-2xl font-bold text-red-600">{formatQty(order.total_lost)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Observações */}
      {order.notes && (
        <Card>
          <CardHeader title="Observações" />
          <CardContent>
            <p className="text-slate-700 whitespace-pre-wrap">{order.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Itens */}
      <Card>
        <CardHeader title="Itens" subtitle="Itens planejados nesta ordem de produção" />
        <CardContent className="p-0">
          {(order.items || []).length === 0 ? (
            <div className="text-center py-8">
              <Factory className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500">Nenhum item na ordem</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">Código</th>
                  <th className="text-left">Descrição</th>
                  <th className="text-center">Tipo</th>
                  <th className="text-right">Planejado</th>
                  <th className="text-right">Produzido</th>
                  <th className="text-right">Perdido</th>
                  <th className="text-left">Observações</th>
                </tr>
              </TableHeader>
              <tbody>
                {order.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                        {item.target_code || '-'}
                      </code>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-slate-900">{item.target_description || '-'}</p>
                    </TableCell>
                    <TableCell className="text-center">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.product_id
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {item.product_id ? 'Produto' : 'Semiacabado'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium text-slate-900">
                        {formatQty(item.quantity_planned)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium text-emerald-700">
                        {formatQty(item.quantity_produced)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {item.quantity_lost ? (
                        <span className="font-medium text-red-600">
                          {formatQty(item.quantity_lost)}
                        </span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">{item.notes || '-'}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pedidos de venda vinculados */}
      <Card>
        <CardHeader
          title="Pedidos de Venda Vinculados"
          subtitle="Pedidos que originaram esta ordem de produção"
        />
        <CardContent className="p-0">
          {(order.sales_orders || []).length === 0 ? (
            <div className="text-center py-6">
              <p className="text-slate-500 text-sm">Nenhum pedido de venda vinculado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">Pedido</th>
                  <th className="text-center">Data</th>
                  <th className="text-left">Cliente</th>
                </tr>
              </TableHeader>
              <tbody>
                {order.sales_orders.map((so) => (
                  <TableRow key={so.order_id}>
                    <TableCell>
                      <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                        {getOrderNumber(so)}
                      </code>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm text-slate-700">{formatDate(so.order_date)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-700">{so.customer_name || '-'}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Apontamentos da OP */}
      <Card>
        <CardHeader
          title="Apontamentos"
          subtitle="Apontamentos de produção registrados nesta OP"
        />
        <CardContent className="p-0">
          {logsLoading ? (
            <div className="text-center py-6">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
              <p className="text-slate-500 text-sm">Carregando apontamentos...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-6">
              <Timer className="w-10 h-10 text-slate-400 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">Nenhum apontamento registrado nesta OP</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">Data</th>
                  <th className="text-left">Colaborador</th>
                  <th className="text-left">Atividade</th>
                  <th className="text-right">OK</th>
                  <th className="text-right">Defeitos</th>
                  <th className="text-right">Tempo (min)</th>
                </tr>
              </TableHeader>
              <tbody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <span className="text-sm text-slate-700">{formatDate(log.log_date)}</span>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-slate-900">{log.employee_name || '-'}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {log.activity_code && (
                          <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                            {log.activity_code}
                          </code>
                        )}
                        <span className="text-sm text-slate-700">{log.activity_name || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium text-emerald-700">
                        {formatQty(log.quantity_ok)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {log.quantity_defect ? (
                        <span className="font-medium text-red-600">
                          {formatQty(log.quantity_defect)}
                        </span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm text-slate-700">
                        {log.time_spent_minutes ? formatQty(log.time_spent_minutes) : '-'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal de conclusão */}
      <Dialog
        isOpen={showCompleteModal}
        onClose={() => !actionLoading && setShowCompleteModal(false)}
        title={`Concluir Produção — ${order.code}`}
        size="xl"
        closeOnBackdrop={!actionLoading}
        showCloseButton={!actionLoading}
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowCompleteModal(false)}
              disabled={actionLoading}
            >
              Cancelar
            </Button>
            <Button
              variant="success"
              onClick={handleRequestComplete}
              disabled={actionLoading}
              icon={<CheckCircle2 size={18} />}
            >
              Concluir Produção
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Alert variant="warning" title="Atenção">
            A conclusão gera movimentos de estoque (entrada da produção, consumo de componentes
            via Lista de Materiais e perda) e não pode ser desfeita.
          </Alert>

          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">Item</th>
                <th className="text-right">Planejado</th>
                <th className="text-right">Produzido</th>
                <th className="text-right">Perdido</th>
              </tr>
            </TableHeader>
            <tbody>
              {completeItems.map((row) => (
                <TableRow key={row.production_order_item_id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">{row.code}</code>
                      <span className="text-sm text-slate-700">{row.description}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-medium text-slate-900">
                      {formatQty(row.quantity_planned)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <input
                      type="number"
                      className="border rounded px-2 py-1 w-24 text-right"
                      min={0}
                      value={row.quantity_produced}
                      onChange={(e) =>
                        updateCompleteItem(
                          row.production_order_item_id,
                          'quantity_produced',
                          e.target.value
                        )
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <input
                      type="number"
                      className="border rounded px-2 py-1 w-24 text-right"
                      min={0}
                      value={row.quantity_lost}
                      onChange={(e) =>
                        updateCompleteItem(
                          row.production_order_item_id,
                          'quantity_lost',
                          e.target.value
                        )
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>

          <Checkbox
            label="Baixar materiais pelo consumo da Lista de Materiais"
            checked={consumeMaterials}
            onChange={(e) => setConsumeMaterials(e.target.checked)}
          />

          {completeError && <p className="text-sm text-red-600">{completeError}</p>}
        </div>
      </Dialog>

      {/* Confirmação de conclusão */}
      <ConfirmDialog
        isOpen={showCompleteConfirm}
        onClose={() => setShowCompleteConfirm(false)}
        onConfirm={handleComplete}
        title="Concluir Produção"
        message={`Confirma a conclusão da ordem ${order.code}? Os movimentos de estoque serão gerados e esta ação não pode ser desfeita.`}
        confirmText="Concluir"
        severity="warning"
        loading={actionLoading}
      />

      {/* Confirmação de início */}
      <ConfirmDialog
        isOpen={showStartConfirm}
        onClose={() => setShowStartConfirm(false)}
        onConfirm={handleStart}
        title="Iniciar Produção"
        message={`Confirma o início da produção da ordem ${order.code}?`}
        confirmText="Iniciar"
        severity="info"
        loading={actionLoading}
      />

      {/* Confirmação de cancelamento */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleCancel}
        title="Cancelar Ordem de Produção"
        message={`Tem certeza que deseja cancelar a ordem ${order.code}? Esta ação não pode ser desfeita.`}
        confirmText="Cancelar OP"
        cancelText="Voltar"
        severity="danger"
        loading={actionLoading}
      />
    </div>
  );
};
